// lib/svg-layer.ts
import type { Rect, SvgPaintChannel, SvgPaintSource, SvgPaintTarget } from '#types/types';
import {
  buildStudioLayerSvgTransform,
  resolveStudioLayerTransform,
} from '#lib/studio-layer-transform';

export const SVG_LAYER_PARAM_KEY = '__faterSvgLayer';
const DRAWING_LAYER_PARAM_KEY = '__faterDrawingLayer';

type SvgLayerSource = 'upload' | 'drawing' | 'vectorized';

export interface SvgLayerPayload {
  svg?: string;
  url?: string;
  width?: number;
  height?: number;
  viewBox?: string;
  source?: SvgLayerSource;
}

const SVG_XMLNS = 'http://www.w3.org/2000/svg';
const SVG_INTERACTIVE_TARGET_ATTR = 'data-fater-svg-paint-target-id';

const SVG_PAINT_CHANNELS: readonly SvgPaintChannel[] = ['fill', 'stroke', 'stop-color'];
export const SVG_CHANNEL_LABELS: Record<SvgPaintChannel, string> = {
  fill: 'Fill',
  stroke: 'Stroke',
  'stop-color': 'Gradient stop',
};

const EMPTY_SVG_PAINT_TARGETS: readonly SvgPaintTarget[] = Object.freeze([]);
const UNSAFE_INLINE_SVG_TAGS = new Set(['script', 'foreignobject', 'iframe', 'object', 'embed', 'link', 'meta']);
const SVG_URL_ATTRS = new Set(['href', 'xlink:href']);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const readFinitePositive = (value: unknown): number | undefined => {
  const parsed = typeof value === 'number'
    ? value
    : (typeof value === 'string' && value.trim().length > 0 ? Number(value) : Number.NaN);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
};

const readString = (value: unknown): string | undefined => {
  const trimmed = typeof value === 'string' ? value.trim() : '';
  return trimmed.length > 0 ? trimmed : undefined;
};

const clamp = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value));

const toFiniteNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

export const normalizeHexInput = (value: string): string | null => {
  const trimmed = value.trim();
  const withHash = trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
  return /^#[0-9a-f]{6}$/i.test(withHash) ? withHash.toUpperCase() : null;
};

const toHexByte = (value: number): string => (
  clamp(Math.round(value), 0, 255).toString(16).padStart(2, '0').toUpperCase()
);

const parseSvgColorComponent = (value: string): number | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (trimmed.endsWith('%')) {
    const parsedPercent = Number(trimmed.slice(0, -1));
    return Number.isFinite(parsedPercent) ? clamp(parsedPercent, 0, 100) * 2.55 : null;
  }
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? clamp(parsed, 0, 255) : null;
};

const parseSvgAlpha = (value: string | undefined): number | null => {
  if (value === undefined) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (trimmed.endsWith('%')) {
    const parsedPercent = Number(trimmed.slice(0, -1));
    return Number.isFinite(parsedPercent) ? clamp(parsedPercent / 100, 0, 1) : null;
  }
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? clamp(parsed, 0, 1) : null;
};

const CSS_COLOR_KEYWORDS: Record<string, string> = {
  black: '#000000',
  white: '#FFFFFF',
  red: '#FF0000',
  green: '#008000',
  blue: '#0000FF',
  yellow: '#FFFF00',
  cyan: '#00FFFF',
  magenta: '#FF00FF',
  gray: '#808080',
  grey: '#808080',
  silver: '#C0C0C0',
  maroon: '#800000',
  olive: '#808000',
  purple: '#800080',
  teal: '#008080',
  navy: '#000080',
  orange: '#FFA500',
  brown: '#A52A2A',
  pink: '#FFC0CB',
};

const normalizeSvgPaintColor = (value: unknown): { color: string; opacity?: number } | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const normalized = trimmed.toLowerCase();
  if (
    normalized === 'none' ||
    normalized === 'currentcolor' ||
    normalized === 'context-fill' ||
    normalized === 'context-stroke' ||
    normalized.startsWith('url(') ||
    normalized.startsWith('var(')
  ) {
    return null;
  }
  if (normalized === 'transparent') {
    return { color: '#000000', opacity: 0 };
  }

  const keywordColor = CSS_COLOR_KEYWORDS[normalized];
  if (keywordColor) {
    return { color: keywordColor };
  }

  const hexMatch = normalized.match(/^#([0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i);
  if (hexMatch) {
    const raw = hexMatch[1] ?? '';
    if (raw.length === 3 || raw.length === 4) {
      const color = `#${raw.slice(0, 3).split('').map((part) => part + part).join('')}`.toUpperCase();
      const alpha = raw[3];
      const opacity = raw.length === 4 && alpha ? parseInt(alpha + alpha, 16) / 255 : undefined;
      return { color, opacity };
    }
    const color = `#${raw.slice(0, 6)}`.toUpperCase();
    const opacity = raw.length === 8 ? parseInt(raw.slice(6, 8), 16) / 255 : undefined;
    return { color, opacity };
  }

  const rgbMatch = trimmed.match(/^rgba?\((.+)\)$/i);
  const rgbBody = rgbMatch?.[1];
  if (rgbBody) {
    const parts = rgbBody
      .replace(/\s*\/\s*/, ',')
      .split(/[\s,]+/)
      .filter(Boolean);
    const [redPart, greenPart, bluePart, alphaPart] = parts;
    if (redPart && greenPart && bluePart) {
      const red = parseSvgColorComponent(redPart);
      const green = parseSvgColorComponent(greenPart);
      const blue = parseSvgColorComponent(bluePart);
      if (red !== null && green !== null && blue !== null) {
        return {
          color: `#${toHexByte(red)}${toHexByte(green)}${toHexByte(blue)}`,
          opacity: parseSvgAlpha(alphaPart) ?? undefined,
        };
      }
    }
  }

  return null;
};

const getSvgPaintOpacityAttribute = (channel: SvgPaintChannel): string => {
  if (channel === 'stroke') return 'stroke-opacity';
  if (channel === 'stop-color') return 'stop-opacity';
  return 'fill-opacity';
};

const getInlineStyleProperty = (styleValue: string | null, property: string): string | null => {
  if (!styleValue) return null;
  const normalizedProperty = property.toLowerCase();
  const declarations = styleValue.split(';');
  for (const declaration of declarations) {
    const separatorIndex = declaration.indexOf(':');
    if (separatorIndex < 0) continue;
    const name = declaration.slice(0, separatorIndex).trim().toLowerCase();
    if (name !== normalizedProperty) continue;
    const value = declaration.slice(separatorIndex + 1).trim();
    return value || null;
  }
  return null;
};

const setInlineStyleProperty = (styleValue: string | null, property: string, nextValue: string): string => {
  const declarations = (styleValue ?? '')
    .split(';')
    .map((declaration) => declaration.trim())
    .filter(Boolean);
  const normalizedProperty = property.toLowerCase();
  let didReplace = false;
  const nextDeclarations = declarations.map((declaration) => {
    const separatorIndex = declaration.indexOf(':');
    if (separatorIndex < 0) return declaration;
    const name = declaration.slice(0, separatorIndex).trim();
    if (name.toLowerCase() !== normalizedProperty) return declaration;
    didReplace = true;
    return `${name}: ${nextValue}`;
  });
  if (!didReplace) {
    nextDeclarations.push(`${property}: ${nextValue}`);
  }
  return nextDeclarations.join('; ');
};

const readSvgPaintOpacity = (
  element: Element,
  channel: SvgPaintChannel,
  source: SvgPaintSource,
  parsedOpacity: number | undefined
): number => {
  const opacityAttribute = getSvgPaintOpacityAttribute(channel);
  const styleValue = element.getAttribute('style');
  const rawOpacity = source === 'style'
    ? getInlineStyleProperty(styleValue, opacityAttribute) ?? element.getAttribute(opacityAttribute)
    : element.getAttribute(opacityAttribute) ?? getInlineStyleProperty(styleValue, opacityAttribute);
  const parsed = toFiniteNumber(rawOpacity);
  if (parsed !== null) return clamp(parsed, 0, 1);
  return clamp(parsedOpacity ?? 1, 0, 1);
};

const describeSvgPaintElement = (element: Element, elementName: string, elementIndex: number): string => {
  const id = element.getAttribute('id')?.trim();
  if (id) return `#${id}`;

  const label = element.getAttribute('aria-label')?.trim();
  if (label) return label;

  const className = element.getAttribute('class')?.trim();
  if (className) return `.${className.split(/\s+/).slice(0, 2).join('.')}`;

  if (elementName === 'stop') {
    const offset = element.getAttribute('offset')?.trim();
    const parentId = element.parentElement?.getAttribute('id')?.trim();
    return parentId
      ? `${parentId}${offset ? ` ${offset}` : ''}`
      : `${elementName} ${elementIndex + 1}${offset ? ` ${offset}` : ''}`;
  }

  return `${elementName} ${elementIndex + 1}`;
};

const buildSvgPaintTargetId = (
  elementIndex: number,
  channel: SvgPaintChannel,
  source: SvgPaintSource
): string => `${elementIndex}:${channel}:${source}`;

const readSvgPaintTarget = (
  element: Element,
  elementIndex: number,
  channel: SvgPaintChannel
): SvgPaintTarget | null => {
  const styleValue = element.getAttribute('style');
  const stylePaint = getInlineStyleProperty(styleValue, channel);
  const attributePaint = element.getAttribute(channel);
  const source: SvgPaintSource | null = stylePaint ? 'style' : attributePaint ? 'attribute' : null;
  const rawPaint = stylePaint ?? attributePaint;
  const parsedPaint = normalizeSvgPaintColor(rawPaint);
  if (!source || !parsedPaint) return null;

  const elementName = element.tagName.toLowerCase();
  const elementLabel = describeSvgPaintElement(element, elementName, elementIndex);
  return {
    id: buildSvgPaintTargetId(elementIndex, channel, source),
    elementIndex,
    elementName,
    elementLabel,
    channel,
    source,
    color: parsedPaint.color,
    opacity: readSvgPaintOpacity(element, channel, source, parsedPaint.opacity),
  };
};

const parseSvgMarkup = (svgMarkup: string): Document | null => {
  if (typeof DOMParser === 'undefined') return null;
  try {
    const doc = new DOMParser().parseFromString(svgMarkup, 'image/svg+xml');
    if (doc.querySelector('parsererror')) return null;
    const root = doc.documentElement;
    return root?.tagName.toLowerCase() === 'svg' ? doc : null;
  } catch {
    return null;
  }
};

const getSvgIndexedElements = (doc: Document): Element[] => {
  const root = doc.documentElement;
  return root ? [root, ...Array.from(root.querySelectorAll('*'))] : [];
};

export const extractSvgPaintTargets = (svgMarkup: string | null | undefined): readonly SvgPaintTarget[] => {
  const normalizedMarkup = typeof svgMarkup === 'string' ? svgMarkup.trim() : '';
  if (!normalizedMarkup) return EMPTY_SVG_PAINT_TARGETS;
  const doc = parseSvgMarkup(normalizedMarkup);
  if (!doc) return EMPTY_SVG_PAINT_TARGETS;

  const targets: SvgPaintTarget[] = [];
  getSvgIndexedElements(doc).forEach((element, elementIndex) => {
    SVG_PAINT_CHANNELS.forEach((channel) => {
      const target = readSvgPaintTarget(element, elementIndex, channel);
      if (target) targets.push(target);
    });
  });

  return targets.length > 0 ? targets : EMPTY_SVG_PAINT_TARGETS;
};

const formatSvgOpacity = (value: number): string => (
  String(Number(clamp(value, 0, 1).toFixed(3)))
);

export const patchSvgPaintTarget = (
  svgMarkup: string,
  targetId: string,
  nextColor: string,
  nextOpacity: number
): string | null => {
  if (typeof XMLSerializer === 'undefined') return null;
  const doc = parseSvgMarkup(svgMarkup);
  if (!doc) return null;

  const [indexPart, channelPart, sourcePart] = targetId.split(':');
  const elementIndex = Number(indexPart);
  const channel = SVG_PAINT_CHANNELS.includes(channelPart as SvgPaintChannel)
    ? channelPart as SvgPaintChannel
    : null;
  const source = sourcePart === 'attribute' || sourcePart === 'style' ? sourcePart : null;
  const normalizedColor = normalizeHexInput(nextColor);
  if (!Number.isInteger(elementIndex) || !channel || !source || !normalizedColor) return null;

  const element = getSvgIndexedElements(doc)[elementIndex];
  if (!element) return null;

  const opacityAttribute = getSvgPaintOpacityAttribute(channel);
  if (source === 'style') {
    const withColor = setInlineStyleProperty(element.getAttribute('style'), channel, normalizedColor);
    element.setAttribute('style', setInlineStyleProperty(withColor, opacityAttribute, formatSvgOpacity(nextOpacity)));
  } else {
    element.setAttribute(channel, normalizedColor);
    element.setAttribute(opacityAttribute, formatSvgOpacity(nextOpacity));
  }

  return new XMLSerializer().serializeToString(doc.documentElement);
};

const sanitizeInteractiveSvgElementTree = (root: Element): void => {
  Array.from(root.querySelectorAll('*')).forEach((element) => {
    if (UNSAFE_INLINE_SVG_TAGS.has(element.tagName.toLowerCase())) {
      element.remove();
    }
  });

  [root, ...Array.from(root.querySelectorAll('*'))].forEach((element) => {
    Array.from(element.attributes).forEach((attribute) => {
      const name = attribute.name.toLowerCase();
      const value = attribute.value.trim().toLowerCase();
      if (name.startsWith('on')) {
        element.removeAttribute(attribute.name);
      } else if (SVG_URL_ATTRS.has(name) && value.startsWith('javascript:')) {
        element.removeAttribute(attribute.name);
      }
    });
  });
};

export const buildInteractiveSvgLayerMarkup = (svgMarkup: string | null | undefined): string | null => {
  if (typeof XMLSerializer === 'undefined') return null;
  const normalizedMarkup = typeof svgMarkup === 'string' ? svgMarkup.trim() : '';
  if (!normalizedMarkup) return null;
  const doc = parseSvgMarkup(normalizedMarkup);
  if (!doc) return null;

  const root = doc.documentElement;
  getSvgIndexedElements(doc).forEach((element, elementIndex) => {
    const target = SVG_PAINT_CHANNELS
      .map((channel) => readSvgPaintTarget(element, elementIndex, channel))
      .find((candidate): candidate is SvgPaintTarget => candidate !== null);
    if (!target) return;
    element.setAttribute(SVG_INTERACTIVE_TARGET_ATTR, target.id);
    element.setAttribute('pointer-events', 'visiblePainted');
  });

  sanitizeInteractiveSvgElementTree(root);
  root.setAttribute('width', '100%');
  root.setAttribute('height', '100%');
  if (!root.hasAttribute('preserveAspectRatio')) {
    root.setAttribute('preserveAspectRatio', 'none');
  }
  let rootStyle = root.getAttribute('style');
  rootStyle = setInlineStyleProperty(rootStyle, 'display', 'block');
  rootStyle = setInlineStyleProperty(rootStyle, 'width', '100%');
  rootStyle = setInlineStyleProperty(rootStyle, 'height', '100%');
  root.setAttribute('style', rootStyle);

  return new XMLSerializer().serializeToString(root);
};

export const getSvgPaintTargetIdFromEventTarget = (target: EventTarget | null): string | null => {
  if (typeof Element === 'undefined' || !(target instanceof Element)) return null;
  return target.closest(`[${SVG_INTERACTIVE_TARGET_ATTR}]`)?.getAttribute(SVG_INTERACTIVE_TARGET_ATTR) ?? null;
};

export const escapeSvgText = (value: unknown): string => (
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
);

export const escapeXmlAttribute = (value: unknown): string => (
  escapeSvgText(value)
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;')
);

export const svgMarkupToDataUrl = (svg: string): string => {
  const encoded = encodeURIComponent(svg)
    .replace(/'/g, '%27')
    .replace(/"/g, '%22');
  return `data:image/svg+xml;charset=utf-8,${encoded}`;
};

const svgDataUrlToMarkup = (source: string): string | null => {
  const trimmed = source.trim();
  if (!trimmed.toLowerCase().startsWith('data:image/svg+xml')) return null;
  const commaIndex = trimmed.indexOf(',');
  if (commaIndex < 0) return null;
  const metadata = trimmed.slice(0, commaIndex).toLowerCase();
  const payload = trimmed.slice(commaIndex + 1);
  try {
    if (metadata.includes(';base64')) {
      if (typeof atob !== 'function') return null;
      return atob(payload);
    }
    return decodeURIComponent(payload);
  } catch {
    return null;
  }
};

export const getSvgLayerPayload = (params: unknown): SvgLayerPayload | null => {
  if (!isRecord(params)) return null;
  const payload = params[SVG_LAYER_PARAM_KEY];
  if (!isRecord(payload)) return null;

  const source = payload.source === 'upload' || payload.source === 'drawing' || payload.source === 'vectorized'
    ? payload.source
    : undefined;

  return {
    svg: readString(payload.svg),
    url: readString(payload.url),
    width: readFinitePositive(payload.width),
    height: readFinitePositive(payload.height),
    viewBox: readString(payload.viewBox),
    source,
  };
};

const hasSvgCompatibleDrawingLayerPayload = (params: unknown): boolean => (
  isRecord(params) && isRecord(params[DRAWING_LAYER_PARAM_KEY])
);

export const resolveSvgLayerInlineMarkup = async (
  layer: { params?: unknown; imageSrc?: string | null }
): Promise<string | null> => {
  const payload = getSvgLayerPayload(layer.params);
  if (payload?.svg) return payload.svg;

  const source = payload?.url || (typeof layer.imageSrc === 'string' ? layer.imageSrc.trim() : '');
  if (!source) return null;

  const dataUrlMarkup = svgDataUrlToMarkup(source);
  if (dataUrlMarkup) return dataUrlMarkup;

  const fetchSvgText = async (url: string): Promise<string | null> => {
    const response = await fetch(url, { credentials: 'same-origin' });
    if (!response.ok) return null;
    const text = await response.text();
    return /<svg\b/i.test(text) ? text : null;
  };

  try {
    const directText = await fetchSvgText(source);
    if (directText) return directText;
  } catch {
    // Fall through to the app proxy. Cross-origin SVG assets often render as images
    // but cannot be read by client-side fetch for native export inlining.
  }

  if (typeof window === 'undefined') return null;
  try {
    return await fetchSvgText(`/api/proxy-bfl?imageUrl=${encodeURIComponent(source)}`);
  } catch {
    return null;
  }
};

const isSvgAssetSource = (source: unknown): boolean => {
  const normalized = typeof source === 'string' ? source.trim().toLowerCase() : '';
  if (!normalized) return false;
  return (
    normalized.startsWith('data:image/svg+xml') ||
    normalized.endsWith('.svg') ||
    normalized.includes('.svg?') ||
    normalized.includes('.svg#')
  );
};

export const isLayerVectorized = (
  layer: { type?: string | null; params?: unknown; imageSrc?: string | null } | null | undefined
): boolean => {
  if (!layer) return false;
  return (
    layer.type === 'svg' ||
    Boolean(getSvgLayerPayload(layer.params)) ||
    hasSvgCompatibleDrawingLayerPayload(layer.params) ||
    isSvgAssetSource(layer.imageSrc)
  );
};

export const parseSvgIntrinsicSize = (svgText: string): {
  width: number;
  height: number;
  viewBox?: string;
} => {
  const svgMatch = svgText.match(/<svg\b[^>]*>/i);
  const svgTag = svgMatch?.[0] ?? '';
  const readAttr = (name: string): string | undefined => {
    const pattern = new RegExp(`${name}\\s*=\\s*["']([^"']+)["']`, 'i');
    const match = svgTag.match(pattern);
    return match?.[1]?.trim();
  };

  const parseLength = (value: string | undefined): number | undefined => {
    if (!value) return undefined;
    const match = value.match(/^-?\d*\.?\d+/);
    if (!match) return undefined;
    const parsed = Number(match[0]);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
  };

  const viewBox = readAttr('viewBox');
  const viewBoxParts = viewBox
    ?.split(/[\s,]+/)
    .map((part) => Number(part))
    .filter((part) => Number.isFinite(part));

  const width = parseLength(readAttr('width')) ?? (viewBoxParts?.length === 4 ? viewBoxParts[2] : undefined);
  const height = parseLength(readAttr('height')) ?? (viewBoxParts?.length === 4 ? viewBoxParts[3] : undefined);

  return {
    width: Math.max(1, Math.round(width ?? 512)),
    height: Math.max(1, Math.round(height ?? 512)),
    viewBox,
  };
};

export const loadSvgImageElement = async (source: string): Promise<HTMLImageElement> => {
  const loadSource = (imageSource: string): Promise<HTMLImageElement> => {
    const image = new Image();
    image.decoding = 'async';
    image.crossOrigin = 'anonymous';
    const loadPromise = new Promise<HTMLImageElement>((resolve, reject) => {
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error('Failed to load SVG layer image.'));
    });
    image.src = imageSource;
    return loadPromise;
  };

  try {
    return await loadSource(source);
  } catch (error) {
    if (source.trim().toLowerCase().startsWith('data:image/svg+xml')) {
      throw error;
    }
    const inlineSvg = await resolveSvgLayerInlineMarkup({ imageSrc: source });
    if (!inlineSvg) {
      throw error;
    }
    return loadSource(svgMarkupToDataUrl(inlineSvg));
  }
};

export const loadSvgIntrinsicSizeFromUrl = async (
  source: string,
  fallback?: { width?: number; height?: number }
): Promise<{ width: number; height: number; viewBox?: string }> => {
  try {
    const image = await loadSvgImageElement(source);
    const width = image.naturalWidth > 0 ? image.naturalWidth : fallback?.width;
    const height = image.naturalHeight > 0 ? image.naturalHeight : fallback?.height;
    return {
      width: Math.max(1, Math.round(width ?? 512)),
      height: Math.max(1, Math.round(height ?? 512)),
    };
  } catch {
    return {
      width: Math.max(1, Math.round(fallback?.width ?? 512)),
      height: Math.max(1, Math.round(fallback?.height ?? 512)),
    };
  }
};

const stripOuterSvg = (svg: string): string => {
  const withoutXml = svg.replace(/<\?xml[\s\S]*?\?>/gi, '').trim();
  const match = withoutXml.match(/<svg\b[^>]*>([\s\S]*?)<\/svg>/i);
  return match?.[1]?.trim() || withoutXml;
};

export const buildNativeSvgLayerFragment = (
  layer: {
    visible?: boolean | null;
    type?: string | null;
    rect: Rect;
    opacity?: number | null;
    params?: unknown;
    imageSrc?: string | null;
  },
  workspaceFrame: Rect,
  exportRect: Rect,
  options?: { inlineSvg?: string | null; inlineViewBox?: string | null },
): string | null => {
  if (layer.visible === false || !isLayerVectorized(layer)) return null;
  const payload = getSvgLayerPayload(layer.params);
  const layerX = workspaceFrame.x + layer.rect.x;
  const layerY = workspaceFrame.y + layer.rect.y;
  const x = layerX - exportRect.x;
  const y = layerY - exportRect.y;
  const width = Math.max(1, layer.rect.w);
  const height = Math.max(1, layer.rect.h);
  const opacity = typeof layer.opacity === 'number' && Number.isFinite(layer.opacity)
    ? Math.max(0, Math.min(1, layer.opacity))
    : 1;
  const transform = buildStudioLayerSvgTransform(
    x,
    y,
    width,
    height,
    resolveStudioLayerTransform(layer.params)
  );
  const attrs = `transform="${escapeXmlAttribute(transform)}"${opacity < 1 ? ` opacity="${opacity}"` : ''}`;

  const inlineSvg = readString(options?.inlineSvg) ?? payload?.svg;
  if (inlineSvg) {
    const viewBox = payload?.viewBox
      || readString(options?.inlineViewBox)
      || `0 0 ${Math.max(1, Math.round(payload?.width ?? width))} ${Math.max(1, Math.round(payload?.height ?? height))}`;
    return [
      `<svg ${attrs} x="0" y="0" width="${width}" height="${height}" viewBox="${escapeXmlAttribute(viewBox)}" overflow="visible">`,
      stripOuterSvg(inlineSvg),
      `</svg>`,
    ].join('\n');
  }

  const source = payload?.url || (typeof layer.imageSrc === 'string' ? layer.imageSrc.trim() : '');
  if (!source) return null;
  const escapedSource = escapeXmlAttribute(source);
  return `<image ${attrs} href="${escapedSource}" width="${width}" height="${height}" preserveAspectRatio="none"/>`;
};

export const buildNativeSvgDocument = ({
  width,
  height,
  viewBoxWidth,
  viewBoxHeight,
  fragments,
}: {
  width: number;
  height: number;
  viewBoxWidth?: number;
  viewBoxHeight?: number;
  fragments: string[];
}): string => {
  const safeWidth = Math.max(1, Math.round(width));
  const safeHeight = Math.max(1, Math.round(height));
  const safeViewBoxWidth = Math.max(1, Number.isFinite(viewBoxWidth) ? Number(viewBoxWidth) : safeWidth);
  const safeViewBoxHeight = Math.max(1, Number.isFinite(viewBoxHeight) ? Number(viewBoxHeight) : safeHeight);
  return [
    `<?xml version="1.0" encoding="UTF-8"?>`,
    `<svg xmlns="${SVG_XMLNS}" width="${safeWidth}" height="${safeHeight}" viewBox="0 0 ${safeViewBoxWidth} ${safeViewBoxHeight}">`,
    ...fragments,
    `</svg>`,
  ].join('\n');
};
